/**
 * phase-forms-workflow-follow-up (Item C) — edit a submission requirement.
 *
 * Editing a BUILT-IN row creates a tenant OVERRIDE (POST override); editing a
 * CUSTOM tenant row updates it in place (PATCH). Editable: status
 * (Required/Conditional/Optional), name, description (notes), activity scope.
 * The identity fields (region/type/templateType) are fixed — changing them
 * would break the shadow — so they are shown read-only for context.
 */
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useState, type ReactElement } from 'react';
import { v2, ApiError, parseApiErrorBody } from '@/api/v2Client';
import type { EditableRequirement, StatusLevel } from './requirementEdit';

export function RequirementEditModal({
  row,
  activities,
  onSaved,
  onCancel,
}: {
  row: EditableRequirement;
  activities: ReadonlyArray<{ code: string; name: string }>;
  onSaved: () => void;
  onCancel: () => void;
}): ReactElement {
  const [status, setStatus] = useState<StatusLevel>(row.status);
  const [conditional, setConditional] = useState<string>(row.conditional ?? '');
  const [name, setName] = useState<string>(row.name);
  const [notes, setNotes] = useState<string>(row.notes ?? '');
  const [activityCode, setActivityCode] = useState<string>(row.activityCode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    // Map the 3-state status → (isRequired bool, conditional text).
    const isRequired = status === 'required';
    const conditionalOut =
      status === 'conditional' ? (conditional.trim() !== '' ? conditional.trim() : 'Conditional') : null;
    try {
      if (row.isBuiltInOrOverride) {
        // Built-in or existing override → create/update a tenant override.
        await v2.submissionRequirements.overrideRequirement({
          catalog: row.catalog,
          baselineId: row.baselineId,
          status: isRequired,
          conditional: conditionalOut,
          name: name.trim(),
          notes: notes.trim() === '' ? null : notes.trim(),
          activityCode: activityCode.trim() === '' ? null : activityCode.trim(),
        });
      } else {
        // Custom tenant row → in-place PATCH (templates only carry custom rows).
        await v2.submissionRequirements.updateTemplate(row.id, {
          templateName: name.trim(),
          isRequired,
          conditional: conditionalOut,
          notes: notes.trim() === '' ? null : notes.trim(),
          activityCode: activityCode.trim() === '' ? null : activityCode.trim(),
        });
      }
      onSaved();
    } catch (e) {
      setError(
        e instanceof ApiError ? (parseApiErrorBody(e)?.message ?? e.message) : 'Failed to save',
      );
      setSaving(false);
    }
  };

  const label = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 12 } as const;
  const input = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid var(--border, #d1d5db)',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box' as const,
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="formTemplates.requirementEdit"
      title={<span id="req-edit-title">Edit requirement</span>}
      ariaLabelledBy="req-edit-title"
      testId="requirement-edit-modal"
      onClose={saving ? undefined : onCancel}
      escDisabled={saving}
      closeOnBackdrop={!saving}
      width="min(480px, 94vw)"
      zIndex={1000}
    >
      <>
        <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
          {row.contextLabel}
          {row.isBuiltInOrOverride && (
            <>
              {' · '}
              <span data-testid="edit-creates-override">edits create a tenant override of the built-in</span>
            </>
          )}
        </p>

        <label style={label}>Name</label>
        <input data-testid="edit-name" style={input} value={name} onChange={(e) => setName(e.target.value)} />

        <label style={label}>Status</label>
        <select
          data-testid="edit-status"
          style={input}
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusLevel)}
        >
          <option value="required">Required</option>
          <option value="conditional">Conditional</option>
          <option value="optional">Optional</option>
        </select>
        {status === 'conditional' && (
          <>
            <label style={label}>Condition (when it&apos;s required)</label>
            <input
              data-testid="edit-conditional"
              style={input}
              value={conditional}
              onChange={(e) => setConditional(e.target.value)}
              placeholder="e.g. Only if an IR was issued"
            />
          </>
        )}

        <label style={label}>Activity scope</label>
        <select
          data-testid="edit-activity"
          style={input}
          value={activityCode}
          onChange={(e) => setActivityCode(e.target.value)}
        >
          <option value="">All activities</option>
          {activities.map((a) => (
            <option key={a.code} value={a.code}>
              {a.name} ({a.code})
            </option>
          ))}
        </select>

        <label style={label}>Description (optional)</label>
        <textarea
          data-testid="edit-notes"
          style={{ ...input, minHeight: 56, resize: 'vertical' }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {error && <div style={{ color: 'var(--red, #dc2626)', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            data-testid="edit-cancel"
            onClick={onCancel}
            disabled={saving}
            style={{ padding: '8px 16px', border: '1px solid var(--border, #d1d5db)', borderRadius: 6, background: 'var(--surface, #fff)', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="edit-save"
            onClick={() => void save()}
            disabled={saving || name.trim() === ''}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 6,
              background: 'var(--accent, #2563eb)',
              color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving || name.trim() === '' ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
